import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'idsk-react-components';
import { useParams } from 'react-router';
import { getTitle, setTitle } from 'slovlex-idsk-react-components';
import {
  validateLegMaterial,
} from '../../../client/legislativnyMaterial/legislativnyMaterialCreateValidateClient';
import {
  updateLegMaterial,
} from '../../../client/legislativnyMaterial/legislativnyMaterialUpdateClient';
import { useClient, useClientStatusContext } from '../../../context/ClientStatusContext';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import LegMaterialCreateStepEnum
  from '../../../model/legislativnyMaterial/LegMaterialCreateStepEnum';
import routes from '../../../routing/routes';
import LegislativnyMaterialDetail
  from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import removeFirstSpecialChar from '../../../function/removeFirstSpecialChar';
import LegislativnyMaterialCreateUpdate
  from '../../../model/legislativnyMaterial/LegislativnyMaterialCreateUpdate';
import BasicDataStepTwo from './legMaterialStepPages/BasicDataStepTwo';
import RelatedDataStepThree from './legMaterialStepPages/RelatedDataStepThree';
import PredkladatelStepFour from './legMaterialStepPages/PredkladatelStepFour';
import CheckDataStepFive from './legMaterialStepPages/CheckDataStepFive';
import scrollToTop from '../../../function/scrollToTop';
import NavigationGuard from '../../NavigationGuardComponent/components/NavigationGuard';

interface Props {
  legislativnyMaterialDetail: LegislativnyMaterialDetail;
  isAdminWfe: boolean;
}

const LegislativnyMaterialUpdate = ({ legislativnyMaterialDetail, isAdminWfe }: Props): ReactElement => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const { clearStatus } = useClientStatusContext();
  const [blocked, setBlocked] = useState(false);
  const [readyToSubmit, setReadyToSubmit] = useState(false);

  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number }>();

  const [updatedMaterial, setUpdatedMaterial] = useState<LegislativnyMaterialCreateUpdate>({
    ...legislativnyMaterialDetail as LegislativnyMaterialCreateUpdate,
    nazov: removeFirstSpecialChar(legislativnyMaterialDetail.nazov),
    activeStep: LegMaterialCreateStepEnum.BASIC_DATA_2,
    zodpovednaInstitucia: legislativnyMaterialDetail.zodpovednaInstitucia,
    zodpovednyPredkladatel: {
      uuid: legislativnyMaterialDetail.zodpovednyPredkladatel?.uuid,
      nazov: legislativnyMaterialDetail.zodpovednyPredkladatel?.nazov,
    },
    dalsiPredkladatelia: legislativnyMaterialDetail.dalsiPredkladatelia.length === 0
      ? [{
        organizacia: null,
        predkladatel: null,
      }] : legislativnyMaterialDetail.dalsiPredkladatelia,
    podnet: legislativnyMaterialDetail.podnet ? legislativnyMaterialDetail.podnet : undefined,
  });

  const [
    originalMaterialDetail] = useState<LegislativnyMaterialCreateUpdate>(updatedMaterial);

  const [predkladatelMap, setPredkladatelMap] = useState(new Map<string, Array<{ uuid: string, nazov: string }>>());
  const updatePredkladatelMap = (data): void => {
    setPredkladatelMap(data);
  };

  useEffect(() => {
    setTitle(getTitle());
  }, [updatedMaterial.activeStep]);

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();
  const {
    wrapClient: validateLegMaterialClient,
    status: validateLegMaterialStatus,
  } = useClient(validateLegMaterial);
  const {
    wrapClient: updateLegMaterialClient,
    status: updateLegMaterialStatus,
  } = useClient(updateLegMaterial);

  const isEqual = (
    obj1: LegislativnyMaterialCreateUpdate,
    obj2: LegislativnyMaterialCreateUpdate,
  ): boolean => JSON.stringify(obj1) === JSON.stringify(obj2);

  useEffect(() => {
    if (!isEqual(originalMaterialDetail, updatedMaterial)) {
      setBlocked(true);
      window.parent.postMessage({
        type: 'block-navigate',
        value: true,
        heading: 'Naozaj si prajete ukončiť úpravu procesu?',
        content: 'Po ukončení úpravy procesu prídete o zmenené údaje upravovaného procesu.',
      }, '*');
    }
  }, [originalMaterialDetail, updatedMaterial]);

  const updateField: SafeParameterSetter<LegislativnyMaterialCreateUpdate> = (fieldName, value) => {
    setUpdatedMaterial(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const handleSubmit = async (): Promise<void> => {
    await updateLegMaterialClient(type, year, index, updatedMaterial);
    setBlocked(false);
    setReadyToSubmit(true);
    window.parent.postMessage({
      type: 'block-navigate',
      value: false,
    }, '*');
    navigate(routes.procesy.detail.withParams(type, year, index));
    setTimeout(() => {
      displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
    }, 300);
  };

  useEffect(() => {
    if (blocked === false && readyToSubmit) {
      navigate(routes.procesy.detail.withParams(type, year, index));
    }
  }, [blocked, displaySuccess, navigate, type, year, index, readyToSubmit]);

  const onChangeStep = async (
    nextStep: LegMaterialCreateStepEnum,
  ): Promise<void> => {
    if (updatedMaterial.activeStep === LegMaterialCreateStepEnum.CHECK_DATA_5
    || updatedMaterial.activeStep === LegMaterialCreateStepEnum.RELATED_DATA_3) {
      updateField('activeStep', nextStep);
    } else if (updatedMaterial.activeStep !== nextStep) {
      await validateLegMaterialClient(updatedMaterial);
      updateField('activeStep', nextStep);
    }
    scrollToTop();
    clearStatus();
  };

  const renderActiveStepPage = (): ReactElement => {
    switch (updatedMaterial.activeStep) {
      case LegMaterialCreateStepEnum.RELATED_DATA_3:
        return (
          <RelatedDataStepThree
            onChangeStep={onChangeStep}
            isUpdate
            legislativnyMaterial={updatedMaterial}
            onUpdateField={updateField}
            loading={validateLegMaterialStatus.loading}
          />
        );
      case LegMaterialCreateStepEnum.PREDKLADATEL_4:
        return (
          <PredkladatelStepFour
            legMaterialDetail={legislativnyMaterialDetail}
            onChangeStep={onChangeStep}
            isUpdate
            isAdminWfe={isAdminWfe}
            legislativnyMaterial={updatedMaterial}
            onUpdateField={updateField}
            onPredkladatelMapUpdate={updatePredkladatelMap}
            predkladatelMap={predkladatelMap}
            loading={validateLegMaterialStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
      case LegMaterialCreateStepEnum.CHECK_DATA_5:
        return (
          <CheckDataStepFive
            onChangeStep={onChangeStep}
            isUpdate
            legislativnyMaterial={updatedMaterial}
            onSubmit={handleSubmit}
            loading={updateLegMaterialStatus.loading}
          />
        );
      default:
        return (
          <BasicDataStepTwo
            onChangeStep={onChangeStep}
            isUpdate
            legislativnyMaterial={updatedMaterial}
            onUpdateField={updateField}
            loading={validateLegMaterialStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
    }
  };

  return (
    <Row>
      <NavigationGuard
        headingModal="Naozaj si prajete ukončiť úpravu procesu?"
        contentModal="Po ukončení úpravy procesu prídete o zmenené údaje upravovaného procesu."
      />
      <Col widthDesktop="three-quarters">
        <div className="legislativny-material-create">
          {renderActiveStepPage()}
        </div>
      </Col>
    </Row>
  );
};

export default LegislativnyMaterialUpdate;
